import React, { useState, useRef, useEffect } from 'react';
import AddWalletForm from '../WalletDropdown/WalletAddForm'
import deleteIcon from '../../../styles/icons/close.png';

import { useTranslation } from "react-i18next";

const WalletDropdown = ({ selectedWallet, onWalletAdd, onWalletDelete, onWalletSelect, wallets = [] }) => {
  const {t} = useTranslation("exchange")
  const [isOpen, setIsOpen] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [walletToDelete, setWalletToDelete] = useState(null);
  const dropdownRef = useRef(null);

  const walletData = wallets.length > 0 ? wallets : [];
  const selected = selectedWallet ?? walletData[0];
  

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowAddForm(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const getWalletIcon = () => '₮';

  const getWalletColor = () => '#10B981'; // Green color for USDT

  const handleDeleteClick = (wallet, e) => {
    e.stopPropagation();
    setWalletToDelete(wallet);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (walletToDelete) {
      onWalletDelete?.(walletToDelete.id);
      setWalletToDelete(null);
      setShowDeleteModal(false);
    }
  };

  const cancelDelete = () => {
    setWalletToDelete(null);
    setShowDeleteModal(false);
  };

  const handleWalletSelect = (wallet) => {
    onWalletSelect?.(wallet);
    setIsOpen(false);
  };

  const handleAddFormSubmit = (newWallet) => {
    onWalletAdd?.(newWallet);
    setShowAddForm(false);
    setIsOpen(false);
  };

  const handleAddFormClose = () => {
    setShowAddForm(false);
  };

  return (
    <>
      <div className="relative w-full" ref={dropdownRef}>
        <button
          className={`w-full flex items-center justify-between rounded-xl px-4 py-3 transition-all duration-300 cursor-pointer border-2 ${
            isOpen ? 'border-blue-500 bg-gray-50' : 'border-transparent bg-gray-100 hover:bg-gray-200'
          }`}
          onClick={() => setIsOpen(!isOpen)}
          type="button"
        >
          {selected && (
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-7 rounded-md flex items-center justify-center text-white font-bold text-sm"
                  style={{ backgroundColor: getWalletColor() }}
                >
                  {getWalletIcon()}
                </div>
                <div className="flex flex-col gap-0.5">
                  <span className="text-base font-semibold text-gray-800">{selected.name}</span>
                  { selected.verified === false && (
                    <span className="ml-2 bg-red-100 text-red-800 text-[10px] px-2 py-0.5 rounded-full font-medium">
                      {t("UNCHECKED")}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
          <span className={`text-xs text-gray-400 transform transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>
            ▼
          </span>
        </button>

        {isOpen && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-lg z-50 max-h-96 overflow-y-auto animate-fadeIn">
            {!showAddForm ? (
              <>
                {/* Список существующих кошельков */}
                {walletData.map((wallet) => (
                  <div
                    key={wallet.id}
                    className={`w-full flex flex-wrap items-center justify-between px-4 py-3 border-b border-gray-100 transition-colors duration-200 
                      ${selected.id === wallet.id ? 'bg-blue-50' : 'hover:bg-gray-100'}`}
                  >
                    <button
                      className="flex-1 flex flex-wrap items-center gap-2 text-left"
                      onClick={() => handleWalletSelect(wallet)}
                    >
                      <div
                        className="w-10 h-7 rounded-md flex items-center justify-center text-white font-bold text-sm"
                        style={{ backgroundColor: getWalletColor() }}
                      >
                        {getWalletIcon()}
                      </div>
                      <div className="flex flex-wrap flex-col gap-0.5">
                        <span className="text-base font-semibold text-gray-800">{wallet.token.slice(0,6)+ '...'}</span>
                      </div>
                      {wallet.name && (
                        <span className="ml-2 bg-green-100 text-green-800 text-[12px] px-2 py-0.5 rounded-full font-medium">
                          {wallet.name.slice(0,10)}
                        </span>
                      )}
                      { wallet.verified === false && (
                        <span className="bg-red-100 text-red-800 text-[10px] px-2 py-0.5 rounded-full font-medium flex-shrink-0">
                          {t("UNCHECKED")}
                        </span>
                      )}
                    </button>
                    
                    <button
                      onClick={(e) => handleDeleteClick(wallet, e)}
                      className="ml-auto text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-full transition-colors flex-shrink-0"
                      title={t("DELETE_WALLET")}
                    >
                      <img 
                        src={deleteIcon} 
                        alt={t("DELETE")}
                        className="w-4 h-4 filter brightness-0 saturate-100"
                        style={{ filter: 'invert(26%) sepia(89%) saturate(1583%) hue-rotate(331deg) brightness(71%) contrast(97%)' }}
                      />
                    </button>
                  </div>
                ))}

                {/* Кнопка добавления кошелька */}
                <button
                  type="button"
                  onClick={() => setShowAddForm(true)}
                  className="w-full flex items-center gap-3 px-4 py-3 border-t border-gray-100 text-blue-500 font-medium hover:bg-gray-100 transition-colors"
                >
                  <span className="w-6 h-6 flex items-center justify-center border-2 border-blue-500 rounded-full font-bold">+</span>
                  <span>{t("ADD_WALLET")}</span>
                </button>
              </>
            ) : (
              /* Форма добавления кошелька */
              <AddWalletForm 
                onClose={handleAddFormClose}
                onSubmit={handleAddFormSubmit}
              />
            )}
          </div>
        )}
      </div>

      {/* Modal подтверждения удаления */}
      {showDeleteModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[999]">
    <div className="bg-white rounded-xl p-6 mx-4 max-w-sm w-full animate-fadeIn">
      <h3 className="text-lg font-semibold text-gray-800 mb-2">
        {t("DELETE_WALLET_ASK")}
      </h3>
      <p className="text-gray-600 mb-4">
        {t("DELETE_WALLET_ARE_YOU_SURE")} {walletToDelete?.token.slice(0,6) + '...'}? {t("THIS_ACTION_CANT_BE_CANCELED")}.
      </p>
      <div className="flex gap-3">
        <button
          onClick={confirmDelete}
          className="flex-1 bg-red-500 text-white rounded-lg py-2.5 font-medium hover:bg-red-600 transition-colors"
        >
          {t("YES_DELETE")}
        </button>
        <button
          onClick={cancelDelete}
          className="flex-1 bg-gray-200 text-gray-800 rounded-lg py-2.5 font-medium hover:bg-gray-300 transition-colors"
        >
          {t("CANCEL")}
        </button>
      </div>
    </div>
  </div>
)}

    </>
  );
};

export default WalletDropdown;